import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import Layout from '../layout/Layout'
import SellContractForm, { clientFieldsFromDetails, vehicleFieldsFromMotorbike } from '../molecules/SellContractForm'
import { CREATE_SELL_CONTRACT, RESET_SELL_CONTRACT_PDF } from 'src/redux/sellContractReducer'
import { FETCH_CLIENT } from 'src/redux/clientReducer'
import { FETCH_MOTORBIKES } from 'src/redux/motorbikesReducer'
import { RootReducer } from 'src/redux/rootReducer'
import { parseOptionalNumericId, useDocumentCreateParams } from 'src/helpers/addVehicleDocument'
import {
  SellContractFormData,
  sellContractFormDefaults,
  sellContractRequestValues
} from 'src/helpers/sellContractFormValidation'

const SellContractCreate = (): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { clientId, vehicleId, returnPath } = useDocumentCreateParams()
  const loading = useSelector((state: RootReducer) => state.sellContractReducer.loading)
  const motorbike = useSelector((state: RootReducer) => {
    return state.motorbikesReducer.motorbikes.find((item) => item.id === vehicleId) ?? null
  })
  const client = useSelector((state: RootReducer) => {
    if (typeof clientId === 'string') {
      return state.clientReducer.client[clientId] ?? null
    }
    return null
  })
  const sellContractPdf = useSelector((state: RootReducer) => state.sellContractReducer.sell_contract_pdf)
  let motorbikeNumericId = 0
  if (motorbike !== null) {
    motorbikeNumericId = parseInt(motorbike.id, 10)
  } else if (typeof vehicleId === 'string') {
    motorbikeNumericId = parseInt(vehicleId, 10)
  }

  useEffect(() => {
    if (typeof clientId === 'string') {
      dispatch(FETCH_CLIENT(clientId))
    }
  }, [dispatch, clientId])

  useEffect(() => {
    if (typeof vehicleId === 'string' && motorbike === null) {
      dispatch(FETCH_MOTORBIKES())
    }
  }, [dispatch, motorbike, vehicleId])

  useEffect(() => {
    return () => {
      dispatch(RESET_SELL_CONTRACT_PDF())
    }
  }, [dispatch])

  useEffect(() => {
    if (sellContractPdf !== null) {
      window.open(sellContractPdf, '_blank')
      navigate(returnPath)
    }
  }, [sellContractPdf, navigate, returnPath])

  const handleSubmit = (values: SellContractFormData): void => {
    const formClientId = parseOptionalNumericId(values.client_id)
    const resolvedClientId = formClientId === 0 ? parseOptionalNumericId(clientId) : formClientId
    if (resolvedClientId === 0) {
      return
    }
    dispatch(
      CREATE_SELL_CONTRACT({
        ...sellContractRequestValues(values),
        client_id: resolvedClientId
      })
    )
  }

  const handleCancel = (): void => {
    navigate(returnPath)
  }

  return (
    <Layout withoutToolbar>
      <div className="flex flex-col justify-start items-start gap-4 h-[calc(100vh-100px)]">
        <div className="self-stretch h-14 min-h-14 bg-Estigroup-primary rounded-lg border border-neutral-200 flex flex-col justify-start items-center">
          <div className="self-stretch flex-1 px-4 py-3 inline-flex justify-start items-center">
            <div className="text-foregrounds-fg-on-color text-xl font-semibold font-Montserrat">
              {t('my_vehicles.document_sell_contract')}
            </div>
          </div>
        </div>
        <SellContractForm
          key={`${clientId ?? ''}-${client === null ? 'pending' : String(client.id)}-${
            motorbike === null ? vehicleId ?? '' : motorbike.id
          }`}
          initialValues={{
            ...sellContractFormDefaults(),
            ...(client === null
              ? {
                  client_id: typeof clientId === 'string' ? parseInt(clientId, 10) : 0,
                  client_name: '',
                  client_email: '',
                  client_phone: '',
                  client_address: ''
                }
              : clientFieldsFromDetails(client)),
            ...(motorbike === null
              ? {
                  motorbike_id: Number.isNaN(motorbikeNumericId) ? 0 : motorbikeNumericId,
                  motorbike_name: '',
                  vin: '',
                  motorbike_year: ''
                }
              : vehicleFieldsFromMotorbike(motorbike))
          }}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          loading={loading}
        />
      </div>
    </Layout>
  )
}

export default SellContractCreate
